import * as React from 'react';
import { cn } from '@/lib/utils/cn';

/**
 * AI confidence chip — load-bearing E10 primitive.
 *
 * Mirrors `prototype/ui.js → confChip()` and the `.chip-ai-conf.{high|med|
 * low}` rules in `prototype/tokens.css`. Used wherever an AI-derived value
 * carries a model confidence — AI proposals, narrative paragraphs, route
 * decisions, register rows, scorecard cells.
 *
 * Visual contract (token-driven, no raw hex):
 *   - same 22px pill shell as the posture chip (base `.chip` rule)
 *   - AI surface background (`--ai-surface`)
 *   - JetBrains Mono font (monospace) so 2-decimal values line up
 *   - color band derived from `value`:
 *       value >= 0.85           → high   (--ai-confidence-high)
 *       0.65 <= value < 0.85    → medium (--ai-confidence-medium)
 *       value < 0.65            → low    (--ai-confidence-low)
 *
 * The 0.85 threshold is the *UI banding* cutoff from
 * `design_handoff_caliber_v2/README.md` §6. It is intentionally separate
 * from `MIN_CITATION_FIDELITY = 0.95` (the base-formation eval-fail gate);
 * see T-0092 §6 ("Risks and unknowns").
 *
 * Display: always 2-decimal via `value.toFixed(2)`.
 *
 * NOTE: The prototype's `confidenceBand` helper uses 0.6 as the med/low
 * boundary; the T-0092 ticket and README §6 both specify 0.65. The TA
 * contract wins per CLAUDE.md §3.
 *
 * Accessibility: `aria-label="AI confidence {value}"`.
 *
 * Required reading: `design_handoff_caliber_v2/README.md` §6
 * (AI confidence chip).
 */
export interface AiConfidenceChipProps {
  /** 0..1 confidence score. Clamped for display safety, but expected in-range. */
  value: number;
  /** Optional class merge — keep minimal; tokens-only by default. */
  className?: string;
}

export type AiConfidenceBand = 'high' | 'medium' | 'low';

/**
 * Map a 0..1 confidence value to its UI band per README §6.
 * Exported so consumers (e.g. AI surfaces, proposal queue rows) can derive
 * the same band without re-implementing the threshold.
 */
export function aiConfidenceBand(value: number): AiConfidenceBand {
  if (value >= 0.85) return 'high';
  if (value >= 0.65) return 'medium';
  return 'low';
}

const BAND_CLASSES: Record<AiConfidenceBand, string> = {
  high: 'text-ai-confidence-high',
  medium: 'text-ai-confidence-medium',
  low: 'text-ai-confidence-low',
};

export function AiConfidenceChip({ value, className }: AiConfidenceChipProps) {
  const band = aiConfidenceBand(value);
  const display = value.toFixed(2);
  return (
    <span
      role="status"
      aria-label={`AI confidence ${display}`}
      title="AI confidence"
      className={cn(
        // Base `.chip` shell (matches `.chip-ai-conf` in prototype tokens.css:
        // AI-surface bg, monospace, 11px font, letter-spacing 0).
        'inline-flex h-[22px] items-center gap-1.5 whitespace-nowrap rounded-full px-2.5',
        'bg-ai-surface font-mono text-[11px] font-medium leading-[18px] tracking-normal',
        BAND_CLASSES[band],
        className,
      )}
    >
      {display}
    </span>
  );
}
